import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../FeatherIcon';
import { GROUP_COLORS, UNGROUPED_ID, sectionOf, type GroupColor, type SidebarLayout } from './sidebarLayout';
import type { MenuTarget } from './pressMenu';

export interface SidebarMenuActions {
  rename: (sectionId: string) => void;
  color: (sectionId: string, color: GroupColor) => void;
  sort: (sectionId: string) => void;
  shift: (sectionId: string, delta: -1 | 1) => void;
  remove: (sectionId: string) => void;
  moveAccount: (accountId: string, sectionId: string) => void;
  newGroupWith: (accountId: string) => void;
}

interface SidebarMenuProps {
  target: MenuTarget;
  x: number;
  y: number;
  layout: SidebarLayout;
  actions: SidebarMenuActions;
  onClose: () => void;
}

/** The ⋯ / right-click / long-press menu of a group heading or an account row. */
export function SidebarMenu({ target, x, y, layout, actions, onClose }: SidebarMenuProps) {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Keep the menu on screen: flip it up/left when it would overflow.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
      top: Math.max(8, y + height > window.innerHeight - 8 ? y - height : y),
    });
  }, [x, y]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const run = (action: () => void) => () => {
    action();
    onClose();
  };

  const index = layout.sections.findIndex((s) => s.id === target.id);
  const section = layout.sections[index];

  return (
    <>
      <div className="sb-pop-scrim sb-menu-scrim" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} aria-hidden="true" />
      <div ref={ref} className="sb-menu" style={pos} role="menu">
        {target.kind === 'account' ? (
          <>
            <div className="sb-menu-label">{t('layout.moveToGroup')}</div>
            {layout.sections.map((s) => {
              const current = sectionOf(layout, target.id) === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={current}
                  className="sb-menu-item"
                  onClick={run(() => actions.moveAccount(target.id, s.id))}
                >
                  <span className="sb-menu-dot" data-color={s.id === UNGROUPED_ID ? 'none' : s.color} />
                  <span className="sb-menu-text">{s.id === UNGROUPED_ID ? t('layout.ungrouped') : s.name}</span>
                  {current && <FeatherIcon name="check" size={14} />}
                </button>
              );
            })}
            <div className="sb-menu-sep" />
            <button type="button" role="menuitem" className="sb-menu-item" onClick={run(() => actions.newGroupWith(target.id))}>
              <FeatherIcon name="folder-plus" size={14} />
              <span className="sb-menu-text">{t('layout.moveToNewGroup')}</span>
            </button>
          </>
        ) : section ? (
          <>
            {section.id !== UNGROUPED_ID && (
              <>
                <button type="button" role="menuitem" className="sb-menu-item" onClick={run(() => actions.rename(section.id))}>
                  <FeatherIcon name="edit-2" size={14} />
                  <span className="sb-menu-text">{t('layout.renameGroup')}</span>
                </button>
                <div className="sb-menu-swatches" role="group" aria-label={t('layout.groupColor')}>
                  {GROUP_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`sb-menu-swatch ${section.color === c ? 'is-on' : ''}`}
                      data-color={c}
                      aria-label={c}
                      aria-pressed={section.color === c}
                      onClick={run(() => actions.color(section.id, c))}
                    />
                  ))}
                </div>
              </>
            )}
            <button type="button" role="menuitem" className="sb-menu-item" onClick={run(() => actions.sort(section.id))}>
              <FeatherIcon name="type" size={14} />
              <span className="sb-menu-text">{t('layout.sortGroupAz')}</span>
            </button>
            <button type="button" role="menuitem" className="sb-menu-item" disabled={index === 0} onClick={run(() => actions.shift(section.id, -1))}>
              <FeatherIcon name="arrow-up" size={14} />
              <span className="sb-menu-text">{t('layout.moveGroupUp')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="sb-menu-item"
              disabled={index === layout.sections.length - 1}
              onClick={run(() => actions.shift(section.id, 1))}
            >
              <FeatherIcon name="arrow-down" size={14} />
              <span className="sb-menu-text">{t('layout.moveGroupDown')}</span>
            </button>
            {section.id !== UNGROUPED_ID && (
              <>
                <div className="sb-menu-sep" />
                <button type="button" role="menuitem" className="sb-menu-item is-danger" onClick={run(() => actions.remove(section.id))}>
                  <FeatherIcon name="trash-2" size={14} />
                  <span className="sb-menu-text">{t('layout.deleteGroup')}</span>
                </button>
              </>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
